import { STATUS } from "@/constants/statuses";
import { CartStore } from "@modules/cart/stores/CartStore";
import { HooksManager } from "@modules/sdk/hooks/HooksManager";
import { from, lastValueFrom, map, switchMap, tap } from "rxjs";
import { create } from "superstruct";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import {
    CartAddItemHookInput,
    CartHooksDefinitions,
} from "../cart_sdk/hooks/CartSdkHooks";
import {
    CART_DRAWER_TAG_NAME,
    CART_FOOTER_ID,
    CART_ICON_BUBBLE_ID_SELECTOR,
    CART_ICON_BUBBLE_TAG_NAME,
    CART_ITEMS_ID,
    EMPTY_CART_CLASSNAME,
    JS_CONTENTS_SELECTOR,
    SHOPIFY_SECTION_CLASS_SELECTOR,
} from "../constants";
import type CartEntity from "../entities/CartEntity";
import type { UpdateCartEntity } from "../entities/UpdateCartEntity";
import { CartSchema } from "../schemas/CartSchema";
import { UpdateCartSchema } from "../schemas/UpdateCartSchema";
import CartManagerCounterUpdatersContext from "../utils/CartManager/CartManagerCounterUpdatersContext";
import CartManagerItemsUpdatersContext from "../utils/CartManager/CartManagerItemsUpdatersContext";
import { CartRepositoryProvider } from "./CartRepositoryProvider";

// CartDrawerElement - custom HTML element (cart-drawer) by theme
interface CartDrawerElement extends HTMLElement {
    getSectionsToRender?: () => { id: string }[];
}
export class CartService {
    constructor(
        private readonly cartRepositoryProvider: CartRepositoryProvider,
        private readonly logger: LoggerInterface,
        private readonly counterUpdatersContext: CartManagerCounterUpdatersContext,
        private readonly itemsUpdatersContext: CartManagerItemsUpdatersContext,
        private readonly cartStore: CartStore,
        private readonly hooksManager: HooksManager
    ) {}
    public async getCart(): Promise<CartEntity> {
        this.cartStore.update((state) => ({
            ...state,
            status:
                state.status === STATUS.VIRGIN
                    ? STATUS.INITIAL_LOADING
                    : STATUS.LOADING,
        }));
        return lastValueFrom(
            from(this.cartRepositoryProvider.getRepository().getCart()).pipe(
                map(({ cart }) => {
                    return create(cart, CartSchema);
                }),
                tap((cart) => {
                    this.cartStore.update((state) => ({
                        ...state,
                        items: cart.items,
                        editItemStatuses: {
                            ...cart.items.reduce((acc, item) => {
                                return {
                                    ...acc,
                                    [item.variant_id]: STATUS.READY,
                                };
                            }, {}),
                        },
                        discountCodes: cart.discount_codes,
                        status: STATUS.READY,
                    }));
                })
            )
        );
    }
    public async addToCart(
        items: {
            variantID: number;
            quantity: number;
            properties?: Record<string, string>;
        }[]
    ): Promise<CartEntity> {
        this.cartStore.update((state) => ({
            ...state,
            editItemStatuses: {
                ...state.editItemStatuses,
                ...items.reduce((acc, item) => {
                    return {
                        ...acc,
                        [item.variantID]: STATUS.LOADING,
                    };
                }, {}),
            },
        }));

        const hookInput: CartAddItemHookInput = {
            items: items,
        };

        const addItemHook = this.hooksManager.findHook(
            CartHooksDefinitions.ADD_ITEM
        );

        return lastValueFrom(
            from(addItemHook.execute(hookInput)).pipe(
                switchMap((modifiedData) =>
                    from(
                        this.cartRepositoryProvider.getRepository().addToCart({
                            items: [...modifiedData.items],
                            sections: this.getSectionsToRender(),
                        })
                    ).pipe(
                        map(({ cart, sections }) => {
                            return {
                                cart: create(cart, CartSchema),
                                sections,
                            };
                        }),
                        tap(({ cart, sections }) => {
                            const cartDrawerSection =
                                sections?.[CART_DRAWER_TAG_NAME];
                            const cartIconBubbleSection =
                                sections?.[CART_ICON_BUBBLE_TAG_NAME];

                            if (cartDrawerSection)
                                this.updateCartDrawerSection(cartDrawerSection);

                            if (cartIconBubbleSection)
                                this.updateIconBubbleSection(
                                    cartIconBubbleSection
                                );

                            this.cartStore.update((state) => ({
                                ...state,
                                items: [...cart.items],
                                editItemStatuses: {
                                    ...state.editItemStatuses,
                                    ...cart.items.reduce((acc, item) => {
                                        return {
                                            ...acc,
                                            [item.variant_id]: STATUS.READY,
                                        };
                                    }, {}),
                                },
                            }));
                        }),
                        map(({ cart }) => cart),
                        switchMap((cart) => {
                            return from(this.applyUpdaters(cart)).pipe(
                                map(() => {
                                    return cart;
                                })
                            );
                        })
                    )
                )
            )
        );
    }

    public updateCart(params: {
        discount?: string;
    }): Promise<UpdateCartEntity> {
        this.cartStore.update((state) => ({
            ...state,
            status:
                state.status === STATUS.VIRGIN
                    ? STATUS.INITIAL_LOADING
                    : STATUS.LOADING,
        }));

        return lastValueFrom(
            from(
                this.cartRepositoryProvider.getRepository().updateCart({
                    discount: params.discount,
                    sections: this.getSectionsToRender(),
                })
            ).pipe(
                map((cart) => create(cart, UpdateCartSchema)),
                tap(({ discount_codes, sections }) => {
                    this.updateCartItemsSection(sections);
                    this.updateCartFooterSection(sections);

                    this.cartStore.update((state) => ({
                        ...state,
                        discountCodes: discount_codes,
                        status: STATUS.READY,
                    }));
                })
            )
        );
    }

    async applyUpdaters(cart: CartEntity): Promise<CartEntity> {
        this.logger.debug("ShopifyCartManager.applyUpdaters starts");
        const [counterUpdaterResult, itemsUpdaterResult] =
            await Promise.allSettled([
                this.counterUpdatersContext.updateCount(cart.items.length),
                this.itemsUpdatersContext.updateItems(cart),
            ]);
        this.logger.debug("ShopifyCartManager.applyUpdaters finish", {
            counterUpdaterStatus: counterUpdaterResult,
            itemsUpdaterStatus: itemsUpdaterResult,
        });

        return cart;
    }

    // TODO: Methods below need refactoring to follow Shopify theme patterns.
    //   getSectionsToRender() {
    //     return [
    //       {
    //         id: 'main-cart-items',
    //         section: document.getElementById('main-cart-items').dataset.id,
    //         selector: '.js-contents',
    //       },
    //       {
    //         id: 'cart-icon-bubble',
    //         section: 'cart-icon-bubble',
    //         selector: '.shopify-section',
    //       },
    //       {
    //         id: 'main-cart-footer',
    //         section: document.getElementById('main-cart-footer').dataset.id,
    //         selector: '.js-contents',
    //       },
    //     ];
    //   }
    getSectionsToRender(): string[] {
        const sections: string[] = [];
        const cartDrawerElement: CartDrawerElement | null =
            document.querySelector(CART_DRAWER_TAG_NAME);

        const cartIconBubbleElement = document.querySelector(
            CART_ICON_BUBBLE_ID_SELECTOR
        );

        const cartFooterElement = document.getElementById(CART_FOOTER_ID);
        const cartItemsElement = document.getElementById(CART_ITEMS_ID);

        if (cartDrawerElement && cartDrawerElement.getSectionsToRender) {
            cartDrawerElement.getSectionsToRender().map((section) => {
                sections.push(section.id);
            });
        } else if (cartIconBubbleElement) {
            sections.push(CART_ICON_BUBBLE_TAG_NAME);
        }

        if (cartFooterElement) {
            sections.push(cartFooterElement.dataset.id ?? "");
        }

        if (cartItemsElement) {
            sections.push(cartItemsElement.dataset.id ?? "");
        }

        return sections;
    }

    updateCartDrawerSection(newSection: string) {
        const sectionElement = document.querySelector(CART_DRAWER_TAG_NAME);

        const newSectionElement = new DOMParser()
            .parseFromString(newSection, "text/html")
            .querySelector(CART_DRAWER_TAG_NAME);

        if (sectionElement && newSectionElement) {
            sectionElement.innerHTML = newSectionElement.innerHTML;

            const cartDrawerElement =
                document.querySelector(CART_DRAWER_TAG_NAME);
            cartDrawerElement?.classList.remove(EMPTY_CART_CLASSNAME);
        }
    }

    updateIconBubbleSection(newSection: string) {
        const sectionElement = document.querySelector(
            CART_ICON_BUBBLE_ID_SELECTOR
        );

        const newSectionElement = new DOMParser()
            .parseFromString(newSection, "text/html")
            .querySelector(SHOPIFY_SECTION_CLASS_SELECTOR);

        if (sectionElement && newSectionElement) {
            sectionElement.innerHTML = newSectionElement.innerHTML;
        }
    }

    updateCartFooterSection(sections: Record<string, string | null>) {
        const cartSelector =
            document.getElementById(CART_FOOTER_ID)?.dataset.id;
        const elementToReplace = document
            .getElementById(CART_FOOTER_ID)
            ?.querySelector(JS_CONTENTS_SELECTOR);

        if (cartSelector) {
            const newElement = new DOMParser()
                .parseFromString(sections[cartSelector] ?? "", "text/html")
                .querySelector(JS_CONTENTS_SELECTOR);

            if (elementToReplace && newElement) {
                elementToReplace.innerHTML = newElement.innerHTML;
            }
        }
    }

    updateCartItemsSection(sections: Record<string, string | null>) {
        const cartSelector = document.getElementById(CART_ITEMS_ID)?.dataset.id;
        const elementToReplace = document
            .getElementById(CART_ITEMS_ID)
            ?.querySelector(JS_CONTENTS_SELECTOR);

        if (cartSelector) {
            const newElement = new DOMParser()
                .parseFromString(sections[cartSelector] ?? "", "text/html")
                .querySelector(JS_CONTENTS_SELECTOR);

            if (elementToReplace && newElement) {
                elementToReplace.innerHTML = newElement.innerHTML;
            }
        }
    }
}
